/**
 * What a login form will refuse, and why — before anything is sent.
 *
 * The server is still the authority: these rules mirror `zCreateRetailerUser` and are not a
 * substitute for it. What they add is TIMING. A refusal that arrives only after Create is
 * pressed, as one banner reading "Validation failed", makes the reader guess which of six
 * inputs it meant and retype a password to find out.
 *
 * Pure strings in, message or null out, so the rules can be tested without a form and the
 * same sentence appears wherever a login is edited.
 */

/**
 * Deliberately not a clever regex.
 *
 * Email validity is famously under-specified and every tightened pattern eventually refuses
 * somebody's real address. This checks the shape the system actually depends on — one `@`,
 * something before it, and a dotted domain after — which is also exactly what the server's
 * `z.string().email()` enforces, so the two cannot disagree about a value.
 *
 * `al_Majlis_buyer@test` fails here for the same reason it failed there: `test` is a bare
 * label with no dot, and nothing can deliver to it.
 */
export function emailProblem(raw: string): string | null {
  const v = raw.trim();
  if (!v) return 'An email address is required.';
  if (/\s/.test(v)) return 'An email address cannot contain spaces.';
  const at = v.indexOf('@');
  if (at < 1 || at !== v.lastIndexOf('@')) return 'An email address needs exactly one @.';
  const domain = v.slice(at + 1);
  if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
    return 'The part after @ needs a domain, like example.com.';
  }
  if (v.length > 254) return 'That address is too long.';
  return null;
}

/**
 * Twelve characters, matching the contract.
 *
 * Length only, and that is a decision rather than an omission: a composition rule — one
 * capital, one digit, one symbol — makes passwords harder to remember without making them
 * meaningfully harder to guess, and this one is typed once by an administrator and replaced
 * by the person at first sign-in anyway.
 */
export function passwordProblem(raw: string, { required = true } = {}): string | null {
  if (!raw) return required ? 'A password is required.' : null;
  if (raw.length < 12) return `At least 12 characters — this is ${raw.length}.`;
  if (raw.length > 200) return 'That password is too long.';
  return null;
}

export function nameProblem(raw: string, what: string): string | null {
  const v = raw.trim();
  if (!v) return `A ${what} is required.`;
  if (v.length > 60) return `A ${what} cannot be longer than 60 characters.`;
  return null;
}

/** Optional everywhere it appears, so an empty one is never a problem. */
export function phoneProblem(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (v.length > 40) return 'That phone number is too long.';
  if (!/^[\d\s+()\-.]+$/.test(v)) return 'A phone number can only hold digits, spaces and + ( ) - .';
  return null;
}

/**
 * The server answers with a path per problem; this turns that into the field it belongs to.
 *
 * Needed because the two do not always use the same word — the wire calls it `roleIds` and
 * the form calls it Roles — and because a server refusal with no home ends up back in the
 * banner this exists to empty.
 */
export function problemsByField(
  errors: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of errors) {
    const key = e.path.split('.')[0] ?? '';
    if (key && !out[key]) out[key] = e.message;
  }
  return out;
}
